import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Publication, Reporter, PaginatedResponse, ApiResponse } from '@pf/shared';
import { apiGet } from '../lib/api';

export interface PublicationOption {
  id: string;
  name: string;
  url: string;
  faviconUrl: string | null;
  categories: string[];
}

export interface PublicationsParams {
  search?: string;
  category?: string;
  sortBy?: 'name' | 'pageRank' | 'reportersCount';
  sortOrder?: 'asc' | 'desc';
  minPageRank?: number;
  limit?: number;
}

export interface PublicationReportersParams {
  q?: string;
  hasEmail?: boolean;
  limit?: number;
}

const PUBLICATIONS_KEY = 'publications';

export function usePublicationsSearch(search: string) {
  return useQuery({
    queryKey: [PUBLICATIONS_KEY, 'search', search],
    queryFn: () =>
      apiGet<{ data: PublicationOption[]; total: number }>(
        '/publications',
        { search, limit: 20 },
      ),
    enabled: search.length >= 1,
    staleTime: 1000 * 60 * 5,
  });
}

export function usePublicationsInfinite(params: PublicationsParams = {}) {
  return useInfiniteQuery({
    queryKey: [PUBLICATIONS_KEY, 'infinite', params],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      apiGet<PaginatedResponse<Publication>>('/publications', {
        page: pageParam,
        limit: params.limit ?? 25,
        search: params.search,
        category: params.category,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        minPageRank: params.minPageRank,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return totalLoaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });
}

export function usePublication(id: string | undefined) {
  return useQuery({
    queryKey: [PUBLICATIONS_KEY, id],
    queryFn: () => apiGet<ApiResponse<Publication>>(`/publications/${id}`),
    enabled: !!id,
  });
}

export function usePublicationCategories() {
  return useQuery({
    queryKey: [PUBLICATIONS_KEY, 'categories'],
    queryFn: () => apiGet<{ categories: string[] }>('/publications/categories'),
    staleTime: 1000 * 60 * 10,
  });
}

export function usePublicationReportersInfinite(
  publicationId: string | undefined,
  params: PublicationReportersParams = {},
) {
  return useInfiniteQuery({
    queryKey: [PUBLICATIONS_KEY, publicationId, 'reporters', 'infinite', params],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      apiGet<PaginatedResponse<Reporter>>(`/publications/${publicationId}/reporters`, {
        page: pageParam,
        limit: params.limit ?? 25,
        q: params.q,
        hasEmail: params.hasEmail,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return totalLoaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !!publicationId,
  });
}
